import { lazy, type ComponentType } from 'react';
import type { RouteObject } from 'react-router-dom';
import { ArrowLeftRight, Banknote, BookOpen, Cloud, FileSpreadsheet, Landmark, Receipt } from 'lucide-react';

/**
 * Routes and navigation owned by the bank / books / AWS-usage pages.
 *
 * The app shell (`App.tsx`) spreads `extraRoutes` into its authenticated route tree and
 * renders `extraNav` in the sidebar, so the two page groups are built independently.
 * `icon` is any `lucide-react` icon component. Pages are code-split with `React.lazy`
 * so none of this module's weight is in the shell's initial bundle.
 */
export interface ExtraNavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: ComponentType<{ className?: string }>;
  /** Sidebar section heading. */
  readonly group: 'Banking' | 'Books' | 'Cloud';
  /** Highlight only on this exact path, not on its sub-paths. */
  readonly end?: boolean;
}

const BankPage = lazy(() => import('@/pages/bank/BankPage'));
const BankAccountsPage = lazy(() => import('@/pages/bank/AccountsPage'));
const ChartOfAccountsPage = lazy(() => import('@/pages/books/ChartOfAccountsPage'));
const AccountLedgerPage = lazy(() => import('@/pages/books/AccountLedgerPage'));
const JournalPage = lazy(() => import('@/pages/books/JournalPage'));
const ReportsPage = lazy(() => import('@/pages/books/ReportsPage'));
const AwsUsagePage = lazy(() => import('@/pages/aws/AwsUsagePage'));
const AwsRebillPage = lazy(() => import('@/pages/aws/AwsRebillPage'));

export const extraRoutes: RouteObject[] = [
  { path: 'bank', element: <BankPage /> },
  { path: 'bank/accounts', element: <BankAccountsPage /> },
  { path: 'books/accounts', element: <ChartOfAccountsPage /> },
  { path: 'books/accounts/:id', element: <AccountLedgerPage /> },
  { path: 'books/journal', element: <JournalPage /> },
  { path: 'books/reports', element: <ReportsPage /> },
  { path: 'aws', element: <AwsUsagePage /> },
  { path: 'aws/rebill', element: <AwsRebillPage /> },
];

export const extraNav: ExtraNavItem[] = [
  { to: '/bank/accounts', label: 'Accounts', icon: Landmark, group: 'Banking' },
  { to: '/bank', label: 'Transactions', icon: ArrowLeftRight, group: 'Banking', end: true },
  { to: '/books/accounts', label: 'Chart of accounts', icon: BookOpen, group: 'Books' },
  { to: '/books/journal', label: 'Journal', icon: Banknote, group: 'Books' },
  { to: '/books/reports', label: 'Reports', icon: FileSpreadsheet, group: 'Books' },
  { to: '/aws', label: 'AWS usage', icon: Cloud, group: 'Cloud' },
  { to: '/aws/rebill', label: 'AWS rebilling', icon: Receipt, group: 'Cloud' },
];
